import React, { useState, useEffect } from 'react';
import {
  AiOutlineEye,
  AiOutlineEyeInvisible,
  AiOutlinePlus,
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlineCheck,
  AiOutlineClose,
  AiOutlineLock,
  AiOutlineUserSwitch,
} from 'react-icons/ai';
import { timeAgo } from '../lib/timeAgo';
import EditPasswordModal from './EditPasswordModal';
import DeleteUserModal from './DeleteUserModal';
import DeactivateUserModal from './DeactivateUserModal';
import ReactivateUserModal from './ReactivateUserModal';
import { apiUrl } from '../config/api';
import './UserManagement.css';

const ROLE_LABEL = { user: 'User', manager: 'Manager', admin: 'Administrator' };
const ROLE_NOTE = {
  manager: 'Managers can create and manage users (reset passwords, deactivate, delete) and restore removed photos. They have no access to Integrations or API keys.',
  admin: 'Administrators can manage users, managers, projects and integrations (including API keys).',
};

const ROLE_HINT = {
  user: 'Uses the app — projects, photos and the camera.',
  manager: 'Also manages users and restores removed photos. No Integrations.',
  admin: 'Full access, including managers, Integrations and API keys.',
};

const UserManagement = ({ user }) => {
  const [showCreateUser, setShowCreateUser] = useState(false);
  // 'user' | 'manager' (any admin) | 'admin' (main admin only)
  const [createRole, setCreateRole] = useState('user');
  // Change-role dialog: the user being changed and the role picked for them.
  const [roleTarget, setRoleTarget] = useState(null);
  const [newRole, setNewRole] = useState('user');
  const [savingRole, setSavingRole] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [reactivatingUserId, setReactivatingUserId] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newName, setNewName] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [editPassword, setEditPassword] = useState('');
  const [showEditPasswordInput, setShowEditPasswordInput] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (user?.isAdmin || user?.isManager) {
      setIsAdmin(true);
      loadUsers();
    }
  }, [user]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 8000); // long enough to read a full explanation
      return () => clearTimeout(timer);
    }
  }, [error]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiUrl('/api/auth/users'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setUsers(data);
      } else {
        setError('Failed to load users');
      }
    } catch (err) {
      setError('Error loading users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newEmail || !newPassword || !newName) {
      setError('Please fill in all fields');
      return;
    }

    try {
      const response = await fetch(apiUrl('/api/auth/create-user'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: newEmail,
          password: newPassword,
          name: newName,
          isAdmin: createRole === 'admin',
          isManager: createRole === 'manager',
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setMessage(`${ROLE_LABEL[createRole]} ${newName} created successfully`);
        setNewEmail('');
        setNewPassword('');
        setNewName('');
        setShowCreateUser(false);
        loadUsers();
      } else {
        setError(data.detail || data.message || 'Failed to create user');
      }
    } catch (err) {
      setError('Error creating user: ' + err.message);
    }
  };

  const handleEditPassword = async () => {
    if (!editPassword) {
      setError('Please enter a password');
      return;
    }

    try {
      const response = await fetch(apiUrl('/api/auth/edit-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: editingUserId,
          newPassword: editPassword,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setMessage('Password updated successfully');
        setEditPassword('');
        setEditingUserId(null);
        setShowEditPassword(false);
        loadUsers();
      } else {
        setError(data.detail || data.message || 'Failed to update password');
      }
    } catch (err) {
      setError('Error updating password: ' + err.message);
    }
  };

  const handleDeactivateUser = async (userId) => {
    try {
      const response = await fetch(apiUrl('/api/auth/deactivate-user'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId }),
      });

      if (response.ok) {
        setMessage('User deactivated successfully');
        loadUsers();
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || 'Failed to deactivate user');
      }
    } catch (err) {
      setError('Error deactivating user: ' + err.message);
    }
  };

  const handleReactivateUser = async (userId) => {
    try {
      const response = await fetch(apiUrl('/api/auth/reactivate-user'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId }),
      });

      if (response.ok) {
        setMessage('User reactivated successfully');
        loadUsers();
        setReactivatingUserId(null);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || 'Failed to reactivate user');
      }
    } catch (err) {
      setError('Error reactivating user: ' + err.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const response = await fetch(apiUrl('/api/auth/delete-user'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId }),
      });

      if (response.ok) {
        setMessage('User deleted successfully');
        loadUsers();
        setDeletingUserId(null);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || 'Failed to delete user');
      }
    } catch (err) {
      setError('Error deleting user: ' + err.message);
    }
  };

  const handleClearPin = async (userId) => {
    try {
      const response = await fetch(apiUrl('/api/auth/clear-pin'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId }),
      });

      if (response.ok) {
        setMessage('PIN cleared — user must set a new PIN on next login');
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || data.message || 'Failed to clear PIN');
      }
    } catch (err) {
      setError('Error clearing PIN: ' + err.message);
    }
  };

  // Managers manage normal users; admins also manage managers; only the main admin can
  // change other administrators. (The server enforces the same rules.)
  const canManage = (u) => {
    if (u.id === user?.id || user?.isSuperAdmin) return true;
    if (u.isAdmin) return false;
    if (u.isManager) return !!user?.isAdmin;
    return !!(user?.isAdmin || user?.isManager);
  };
  const openCreate = (role) => { setCreateRole(role); setError(''); setShowCreateUser(true); };

  // Admins change roles; making or changing admins is the main admin's call. Nobody changes
  // their own role, and the main admin's role is fixed. (The server enforces the same rules.)
  const roleOf = (u) => (u.isAdmin ? 'admin' : u.isManager ? 'manager' : 'user');
  const canChangeRole = (u) =>
    !!user?.isAdmin && u.id !== user?.id && !u.isSuperAdmin && (!u.isAdmin || !!user?.isSuperAdmin);
  const openChangeRole = (u) => { setRoleTarget(u); setNewRole(roleOf(u)); setError(''); };

  const handleChangeRole = async () => {
    if (!roleTarget) return;
    setSavingRole(true);
    try {
      const response = await fetch(apiUrl('/api/auth/change-role'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId: roleTarget.id, role: newRole }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setMessage(data.message || 'Role updated');
        setRoleTarget(null);
        loadUsers();
      } else {
        setError(data.detail || 'Failed to change role');
      }
    } catch (err) {
      setError('Error changing role: ' + err.message);
    } finally {
      setSavingRole(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="um-container">
        <div className="um-error-message">
          <AiOutlineClose size={24} />
          <p>You do not have permission to access user management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="um-container">
      <div className="um-header">
        <h1>User Management</h1>
        <div className="um-header-actions">
          {user?.isSuperAdmin && (
            <button
              className="um-create-btn um-create-btn--secondary"
              onClick={() => openCreate('admin')}
            >
              <AiOutlinePlus size={18} />
              Create Admin
            </button>
          )}
          {user?.isAdmin && (
            <button
              className="um-create-btn um-create-btn--secondary"
              onClick={() => openCreate('manager')}
            >
              <AiOutlinePlus size={18} />
              Create Manager
            </button>
          )}
          <button
            className="um-create-btn"
            onClick={() => openCreate('user')}
          >
            <AiOutlinePlus size={18} />
            Create User
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {message && <div className="um-success-message">{message}</div>}
      {error && <div className="um-error-message">{error}</div>}

      {/* Change Role Modal */}
      {roleTarget && (
        <div className="um-modal-overlay" onClick={() => !savingRole && setRoleTarget(null)}>
          <div className="um-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="um-role-title">
            <h2 id="um-role-title">Change role</h2>
            <p className="um-modal-note">
              {roleTarget.name || roleTarget.email} is currently a{roleOf(roleTarget) === 'admin' ? 'n' : ''}{' '}
              <strong>{ROLE_LABEL[roleOf(roleTarget)].toLowerCase()}</strong>. The change applies straight away.
            </p>
            <div className="um-role-options" role="radiogroup" aria-label="New role">
              {['user', 'manager', ...(user?.isSuperAdmin ? ['admin'] : [])].map((r) => (
                <label key={r} className={`um-role-option${newRole === r ? ' um-role-option--selected' : ''}`}>
                  <input type="radio" name="um-role" value={r} checked={newRole === r} onChange={() => setNewRole(r)} disabled={savingRole} />
                  <span className="um-role-option-text">
                    <span className="um-role-option-title">{ROLE_LABEL[r]}</span>
                    <span className="um-role-option-sub">{ROLE_HINT[r]}</span>
                  </span>
                </label>
              ))}
            </div>
            {roleOf(roleTarget) === 'admin' && newRole !== 'admin' && (
              <p className="um-modal-note um-modal-note--warn">Their API keys will be revoked — only administrators can hold API keys.</p>
            )}
            <div className="um-modal-actions">
              <button className="um-modal-cancel" onClick={() => setRoleTarget(null)} disabled={savingRole}>Cancel</button>
              <button className="um-modal-submit" onClick={handleChangeRole} disabled={savingRole || newRole === roleOf(roleTarget)}>
                {savingRole ? 'Saving…' : 'Change role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <div className="um-modal-overlay">
          <div className="um-modal">
            <h2>Create New {ROLE_LABEL[createRole]}</h2>
            {createRole !== 'user' && (
              <p className="um-modal-note">{ROLE_NOTE[createRole]}</p>
            )}
            <div className="um-form-group">
              <label>Email</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            <div className="um-form-group">
              <label>Name</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="John Doe"
              />
            </div>
            <div className="um-form-group">
              <label>Password</label>
              <div className="um-password-input">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  className="um-toggle-password"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <AiOutlineEyeInvisible />
                  ) : (
                    <AiOutlineEye />
                  )}
                </button>
              </div>
            </div>
            <div className="um-modal-actions">
              <button
                className="um-modal-cancel"
                onClick={() => setShowCreateUser(false)}
              >
                Cancel
              </button>
              <button
                className="um-modal-submit"
                onClick={handleCreateUser}
              >
                Create {createRole === 'admin' ? 'Admin' : ROLE_LABEL[createRole]}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      {loading ? (
        <div className="um-loading">Loading users...</div>
      ) : users.length === 0 ? (
        <div className="um-empty">No users found</div>
      ) : (
        <div className="um-table-container">
          <table className="um-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Last login</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className={!u.isActive ? 'um-row--inactive' : ''}>
                  <td data-label="Email">{u.email}</td>
                  <td data-label="Name">{u.name}</td>
                  <td data-label="Role">{u.isSuperAdmin ? 'Main administrator' : u.isAdmin ? 'Administrator' : u.isManager ? 'Manager' : 'User'}</td>
                  <td
                    data-label="Last login"
                    className={u.lastLoginAt ? '' : 'um-never'}
                    title={u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : 'No sign-in recorded since this was added'}
                  >
                    {timeAgo(u.lastLoginAt)}
                  </td>
                  <td data-label="Status">
                    <span
                      className={`um-status ${
                        u.isActive ? 'um-status--active' : 'um-status--inactive'
                      }`}
                    >
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="um-actions" data-label="Actions">
                    {!canManage(u) ? (
                      <span className="um-actions-locked">{u.isAdmin ? 'Main admin only' : 'Admins only'}</span>
                    ) : (<>
                    <button
                      className="um-action-btn um-action-btn--edit"
                      onClick={() => {
                        setEditingUserId(u.id);
                        setShowEditPassword(true);
                      }}
                      data-tooltip="Edit password"
                      type="button"
                    >
                      <AiOutlineEdit size={16} />
                    </button>
                    {u.isActive ? (
                      <button
                        className="um-action-btn um-action-btn--deactivate"
                        onClick={() => handleDeactivateUser(u.id)}
                        data-tooltip={u.id === user?.id ? "Cannot deactivate yourself" : "Deactivate user"}
                        type="button"
                        disabled={u.id === user?.id}
                        style={u.id === user?.id ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                      >
                        <AiOutlineClose size={16} />
                      </button>
                    ) : (
                      <button
                        className="um-action-btn um-action-btn--reactivate"
                        onClick={() => {
                          setReactivatingUserId(u.id);
                          setShowReactivateConfirm(true);
                        }}
                        data-tooltip="Reactivate user"
                        type="button"
                        disabled={u.id === user?.id}
                        style={u.id === user?.id ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                      >
                        <AiOutlineCheck size={16} />
                      </button>
                    )}
                    <button
                      className="um-action-btn um-action-btn--delete"
                      onClick={() => {
                        setDeletingUserId(u.id);
                        setShowDeleteConfirm(true);
                      }}
                      data-tooltip={u.id === user?.id ? "Cannot delete yourself" : "Delete user"}
                      type="button"
                      disabled={u.id === user?.id}
                      style={u.id === user?.id ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    >
                      <AiOutlineDelete size={16} />
                    </button>
                    <button
                      className="um-action-btn um-action-btn--clear-pin"
                      onClick={() => handleClearPin(u.id)}
                      data-tooltip="Clear PIN (forces re-setup on next login)"
                      type="button"
                    >
                      <AiOutlineLock size={16} />
                    </button>
                    {canChangeRole(u) && (
                      <button
                        className="um-action-btn um-action-btn--role"
                        onClick={() => openChangeRole(u)}
                        data-tooltip="Change role"
                        aria-label={`Change role of ${u.name || u.email}`}
                        type="button"
                      >
                        <AiOutlineUserSwitch size={16} />
                      </button>
                    )}
                    </>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Password Modal */}
      {showEditPassword && (
        <EditPasswordModal
          open={showEditPassword}
          editingUserId={editingUserId}
          users={users}
          error={error}
          message={message}
          editPassword={editPassword}
          showEditPasswordInput={showEditPasswordInput}
          loading={loading}
          onClose={() => {
            setShowEditPassword(false);
            setEditPassword('');
            setEditingUserId(null);
          }}
          onSubmit={(e) => {
            e.preventDefault();
            handleEditPassword(editingUserId);
          }}
          onPasswordChange={setEditPassword}
          onTogglePasswordVisibility={() => setShowEditPasswordInput(!showEditPasswordInput)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <DeleteUserModal
          onClose={() => {
            setShowDeleteConfirm(false);
            setDeletingUserId(null);
          }}
          onConfirm={() => {
            handleDeleteUser(deletingUserId);
            setShowDeleteConfirm(false);
          }}
        />
      )}

      {/* Reactivate Confirmation Modal */}
      {showReactivateConfirm && (
        <ReactivateUserModal
          open={showReactivateConfirm}
          reactivatingUserId={reactivatingUserId}
          users={users}
          loading={loading}
          error={error}
          message={message}
          onClose={() => {
            setShowReactivateConfirm(false);
            setReactivatingUserId(null);
          }}
          onConfirm={(userId) => {
            handleReactivateUser(userId);
            setShowReactivateConfirm(false);
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;
